import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CampTable from '../components/camps/CampTable';
import CampMap, { MapBounds } from '../components/camps/CampMap';
import FilterBar, { FilterOptions } from '../components/FilterBar';
import { useCampContext } from '../context/CampContext';
import { useUserContext } from '../context/UserContext';
import { useGetFavorites, useGetSchedules, useGetMapPoints } from '../api';
import { FilterOptionsUtils } from '../utils/filterOptionsUtils';
import { FilterInfo } from '../utils/types';
import { useAnalytics } from '../context/AnalyticsContext';
import './Results.css';
import { MapBoundsParams, SortParams, useGetCampsWithBounds } from '../api/camps';

const Results: React.FC = () => {
  const { camps, setCamps, filters, setFilters, setFavorites, setSchedules, currentScheduleUuid, setCurrentScheduleUuid } = useCampContext();
  const [bounds, setBounds] = useState<MapBoundsParams | null>(null);
  const [sort, setSort] = useState<SortParams>({});

  const { isLoggedIn } = useUserContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredOrganizerId, setHoveredOrganizerId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { trackPageView, trackEvent } = useAnalytics();

  // Fetch favorites and schedules from BE on page load
  const { data: favoritesData } = useGetFavorites('1', { enabled: isLoggedIn });
  const { data: schedulesData } = useGetSchedules({ enabled: isLoggedIn });

  // Fetch map points based on current filters
  const [filtersReady, setFiltersReady] = useState(false);

  const convertedFilters: FilterInfo = FilterOptionsUtils.convertFilterOptionsToFilterInfo(filters);
  const { data: mapPointsData } = useGetMapPoints(convertedFilters, bounds, filtersReady);
  const {
    data: filteredCampsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useGetCampsWithBounds(convertedFilters, bounds, 50, filtersReady, sort);

  useEffect(()=>{
    window.scrollTo(0, 0);
  },[])

  useEffect(() => {
    trackPageView('/search', 'Search Results');
  }, [trackPageView]);

  useEffect(() => {
    if (favoritesData?.camps?.length) {
      setFavorites(favoritesData.camps);
    }
  }, [favoritesData, setFavorites]);

  useEffect(() => {
    if (schedulesData) {
      const myCalendarsData = schedulesData.my_calendars || [];
      const sharedCalendarsData = schedulesData.shared_calendars || [];
      const allSchedules = [...myCalendarsData, ...sharedCalendarsData];

      setSchedules(allSchedules);
      // Set current schedule to first one if not set
      if (!currentScheduleUuid && allSchedules.length > 0) {
        setCurrentScheduleUuid(allSchedules[0].uuid);
      }
    }
  }, [schedulesData, currentScheduleUuid, setSchedules, setCurrentScheduleUuid]);

  useEffect(() => {
    if (filteredCampsData?.pages) {
      const allCamps = filteredCampsData.pages.flatMap(page => page.camps);
      setCamps(allCamps);
    }
    setIsLoading(false);
  }, [filteredCampsData, setCamps]);  

  // Apply query parameters as filters when page loads
  useEffect(() => {
    const applyQueryParamsAsFilters = async () => {
      // Only apply once per page load
      if (filtersReady) {        
        return;
      }

      const hasSearchParams = searchParams.toString().length > 0;
      const hasContextFilters = filters && Object.keys(filters).length > 0;
      
      if (!hasSearchParams && hasContextFilters) {
        const searchParamsFromFilters = FilterOptionsUtils.filtersToSearchParams(filters);
        setSearchParams(searchParamsFromFilters, { replace: true });
        return;
      }
      
      const filtersFromSearchParams = FilterOptionsUtils.searchParamsToFilters(searchParams);
      setFilters(filtersFromSearchParams);
      setIsLoading(true);
      setFiltersReady(true);
    };

    applyQueryParamsAsFilters();
  }, [searchParams, filters, setCamps, setFilters]);

  // Scroll detection for filter bar visibility (only on screens >= 1400px)
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const screenWidth = window.innerWidth;
      
      // Only enable scroll behavior on screens >= 1400px
      if (screenWidth >= 1400) {
        setIsScrolled(scrollTop > 100);
      } else {
        setIsScrolled(false);
      }
    };

    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 1400) {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleMapBoundsChange = useCallback((bounds: MapBounds) => {
    setBounds({
      ne_lat: bounds.ne_lat,
      ne_lng: bounds.ne_lng,
      sw_lat: bounds.sw_lat,
      sw_lng: bounds.sw_lng,
      zoom: bounds.zoom
    });
  }, []);  

  const handleFiltersChange = async (filters: FilterOptions) => {
    console.log('Filters changed:', filters);
    trackEvent('results_page_filters_changed', { filters: filters });
    setIsLoading(true);
    setFilters(filters);
    const searchParamsFromFilters = FilterOptionsUtils.filtersToSearchParams(filters);
    setSearchParams(searchParamsFromFilters, { replace: true });
  };

  const clearAllFiltersAndURL = async () => {
    console.log('Clearing all filters and resetting URL');
    trackEvent('results_page_clear_all_filters_clicked');
    const defaultFilters: FilterOptions = {};
    setIsLoading(true);
    setFilters(defaultFilters);
    const searchParamsFromFilters = FilterOptionsUtils.filtersToSearchParams(filters);
    setSearchParams(searchParamsFromFilters, { replace: true });
  };

  return (
    <div className="results-page">
      <Header 
        onFiltersChange={handleFiltersChange}
        onClearAll={clearAllFiltersAndURL}
        showFilterBar={true}
      />
      
      <div className={`standalone-filter-section ${isScrolled ? 'hidden' : ''}`}>
        <FilterBar 
          onFiltersChange={handleFiltersChange}
          onClearAll={clearAllFiltersAndURL}
        />
      </div>
      
      <main className="results-main">
        <div className="results-container">
          <div className="results-column table-column">
            <CampTable
              isLoading={isLoading}
              onLoadMore={() => fetchNextPage()}
              hasMorePages={hasNextPage ?? false}
              isLoadingMore={isFetchingNextPage}
              totalCamps={filteredCampsData?.pages?.[0]?.pagination?.total ?? 0}
              onMouseEnter={(organizerId) => setHoveredOrganizerId(organizerId ?? null)}
              onMouseLeave={() => setHoveredOrganizerId(null)}
              onFiltersChange={handleFiltersChange}
              onClearAll={clearAllFiltersAndURL}
              onSortChange={setSort}
            />
          </div>
          
          <div className="results-column map-column">
            <div className="map-section">
              <CampMap
                mapPoints={mapPointsData?.points}
                className="results-map"
                hoveredOrganizerId={hoveredOrganizerId}
                onBoundsChange={handleMapBoundsChange}
              />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Results;
